/**
 * Cart Edit Indicator
 * Adds small "edit" indicators to PitchPrint design thumbnails in the cart
 * so customers know they can click to edit their designs
 */

window.CartEditIndicator = (function() {
  'use strict';
  
  let isInitialized = false;
  
  const config = {
    // Selector for PitchPrint images in cart
    pitchprintImageSelector: 'img[src*="pitchprint.io"]',
    // Container selector for cart items
    cartItemSelector: '.cart-item__media, .cart-item__image-container',
    // Edit indicator class
    editIndicatorClass: 'pp-edit-indicator',
    debugMode: false
  };
  
  function log(...args) {
    if (config.debugMode) {
      console.log('[CartEditIndicator]', ...args);
    }
  }
  
  // Extract PitchPrint design ID from image
  function extractDesignId(img) {
    // From image ID like "p_2025-8-17-22-8-cd43a4d0484b2fd293a12c3dd37bb41dcc"
    if (img.id && img.id.startsWith('p_')) {
      return img.id.substring(2); // Remove "p_" prefix
    }
    
    // From image src URL
    const srcMatch = img.src.match(/\/([a-f0-9-]+)_\d+\.jpg/);
    if (srcMatch) {
      return srcMatch[1];
    }
    
    return null;
  }
  
  // Extract product ID from cart item
  function extractProductId(cartItem) {
    if (!cartItem) return null;
    
    // From data-cpcproducturl attribute
    const linkWithProductUrl = cartItem.querySelector('[data-cpcproducturl]');
    if (linkWithProductUrl) {
      return linkWithProductUrl.getAttribute('data-cpcproducturl');
    }
    
    // From product link href
    const productLink = cartItem.querySelector('a[href*="/products/"]');
    if (productLink) {
      const variantMatch = productLink.href.match(/variant=(\d+)/);
      if (variantMatch) {
        return variantMatch[1];
      }
    }
    
    return null;
  }
  
  // Create the edit indicator element
  function createEditIndicator() {
    const indicator = document.createElement('div');
    indicator.className = config.editIndicatorClass;
    indicator.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Z" fill="currentColor"/>
        <path d="m5.25 10.25 2.5 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      <span>Edit</span>
    `;
    indicator.title = 'Click to edit your design';
    return indicator;
  }
  
  // Add edit indicator to a PitchPrint image
  function addEditIndicator(img) {
    // Find the appropriate container
    let container = img.parentNode;
    
    // Look for a container that we can position relative to
    while (container && !container.classList.contains('cart-item__image-container')) {
      container = container.parentNode;
      if (!container || container.tagName === 'TD') break;
    }
    
    if (!container) {
      container = img.parentNode;
    }
    
    // Check if indicator already exists in the final container
    const existingIndicator = container.querySelector('.' + config.editIndicatorClass);
    if (existingIndicator) {
      log('Edit indicator already exists for image');
      return false;
    }
    
    // Ensure container has relative positioning (inline only)
    const computedStyle = window.getComputedStyle(container);
    if (computedStyle.position === 'static') {
      container.style.position = 'relative';
    }
    
    // Create and add the indicator
    const indicator = createEditIndicator();
    container.appendChild(indicator);
    
    // Set up click handler - handle both anchor links and direct image clicks
    const cartItem = img.closest('.cart-item');
    let clickTarget = null;
    
    // Look for the cart item link (might be a sibling)
    if (cartItem) {
      clickTarget = cartItem.querySelector('a.cart-item__link[href]') || 
                   cartItem.querySelector('a[data-cpcproducturl]') ||
                   img.closest('a[href]');
    }
    
    // Fallback to image click if no link found
    if (!clickTarget) {
      clickTarget = img;
    }
    
    indicator.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Extract design ID from image ID or src
      const designId = extractDesignId(img);
      const productId = extractProductId(cartItem);
      
      // Try to launch PitchPrint editor directly
      if (designId && typeof window.ppclient !== 'undefined') {
        // Launch PitchPrint editor for this specific design
        window.ppclient.launchEditor(designId, productId);
      } else if (typeof window.pp !== 'undefined' && window.pp.launchEditor) {
        // Alternative PitchPrint API
        window.pp.launchEditor(designId, productId);
      } else if (img.onclick) {
        // If PitchPrint has attached a click handler to the image, use that
        img.onclick();
      } else {
        // Fallback: click the image directly to trigger PitchPrint
        img.click();
      }
    });
    
    log('Added edit indicator to PitchPrint image');
    return true;
  }
  
  // Find and process all PitchPrint images in cart
  function processCartImages() {
    const pitchprintImages = document.querySelectorAll(config.pitchprintImageSelector);
    let indicatorsAdded = 0;
    
    pitchprintImages.forEach(img => {
      // Check if this image is in the cart (not just any pitchprint image)
      const inCart = img.closest('.cart-item') || 
                    img.closest('[class*="cart"]') || 
                    window.location.pathname.includes('/cart');
      
      if (inCart) {
        if (addEditIndicator(img)) {
          indicatorsAdded++;
        }
      }
    });
    
    if (indicatorsAdded > 0) {
      log(`Added ${indicatorsAdded} edit indicators to cart images`);
    }
    
    return indicatorsAdded;
  }
  
  // Initialize the edit indicators
  function init() {
    if (isInitialized) {
      log('Cart Edit Indicators already initialized');
      return;
    }
    
    log('Initializing Cart Edit Indicators...');
    
    // Add CSS styles if not already added
    if (!document.querySelector('#cart-edit-indicator-styles')) {
      const styles = document.createElement('style');
      styles.id = 'cart-edit-indicator-styles';
      styles.textContent = `
        .${config.editIndicatorClass} {
          position: absolute;
          top: 5px;
          right: 5px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 4px 6px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 3px;
          cursor: pointer;
          z-index: 10;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .${config.editIndicatorClass}:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: scale(1.05);
        }
        
        .${config.editIndicatorClass} svg {
          width: 12px;
          height: 12px;
        }
        
        .${config.editIndicatorClass} span {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        /* Animation for new indicators */
        .${config.editIndicatorClass} {
          animation: fadeInBounce 0.4s ease-out;
        }
        
        @keyframes fadeInBounce {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          60% {
            transform: scale(1.1);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .${config.editIndicatorClass} {
            font-size: 10px;
            padding: 3px 5px;
          }
          
          .${config.editIndicatorClass} svg {
            width: 10px;
            height: 10px;
          }
        }
      `;
      document.head.appendChild(styles);
      log('Added CSS styles for edit indicators');
    }
    
    // Process existing images
    processCartImages();
    
    // Set up observers for dynamic content
    setupObservers();
    
    isInitialized = true;
    log('Cart Edit Indicators initialized');
  }
  
  // Set up mutation observer for dynamic cart updates
  function setupObservers() {
    // Find cart container to scope observation
    const cartContainer = document.querySelector('#main-cart-items') || 
                         document.querySelector('.cart__contents') ||
                         document.querySelector('[class*="cart"]') ||
                         document.body;
    
    // Observer for cart updates
    const observer = new MutationObserver(function(mutations) {
      let shouldProcess = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if new cart items or images were added
              if (node.matches && (
                    node.matches('.cart-item') || 
                    node.matches('img[src*="pitchprint.io"]') ||
                    node.classList.contains('cart-item__media')
                  )) {
                shouldProcess = true;
              } else if (node.querySelector && (
                         node.querySelector('.cart-item') ||
                         node.querySelector('img[src*="pitchprint.io"]')
                       )) {
                shouldProcess = true;
              }
            }
          });
        }
        
        // Also check for attribute changes on images (src changes)
        if (mutation.type === 'attributes' && 
            mutation.target.tagName === 'IMG' && 
            mutation.attributeName === 'src' &&
            mutation.target.src.includes('pitchprint.io')) {
          shouldProcess = true;
        }
      });
      
      if (shouldProcess) {
        log('Cart content updated, reprocessing images');
        setTimeout(processCartImages, 100);
      }
    });
    
    // Observe only the cart container
    observer.observe(cartContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src']
    });
    
    log('Set up observers for dynamic cart updates on', cartContainer.tagName + (cartContainer.id ? '#' + cartContainer.id : ''));
  }
  
  // Public API
  return {
    init: init,
    process: processCartImages,
    config: config
  };
})();

// Auto-initialize when on cart page or when DOM is ready
function autoInitCartEditIndicator() {
  // Check if we're on a cart page or if cart content is present
  const isCartPage = window.location.pathname.includes('/cart') || 
                     document.querySelector('.cart-item') ||
                     document.querySelector('[class*="cart"]');
  
  if (isCartPage) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', window.CartEditIndicator.init);
    } else {
      window.CartEditIndicator.init();
    }
  }
}

// Run auto-initialization
autoInitCartEditIndicator();

// Also make it available globally for manual initialization
if (typeof module !== 'undefined' && module.exports) {
  module.exports = window.CartEditIndicator;
}